import { Button, ButtonProps, Space, Dropdown, MenuProps } from 'antd';
import { ReactNode } from 'react';
import AccessButton from '@/components/AccessButton';
import { useStore } from '@/stores';
import { contains } from '@/utils';
import { observer } from 'mobx-react';

type ButtonType = 'common' | 'delete' | 'edit';

export interface BaseButtonConfig {
  type?: ButtonType;
  danger?: boolean;
  text?: ReactNode;
  show?: boolean | (() => boolean);
  disabled?: boolean | (() => boolean);
  onClick?: () => void | Promise<void>;
  permission?: string[];
}

interface OperatorBtnsProps {
  btns: BaseButtonConfig[];
}

const OperatorBtns = ({ btns }: OperatorBtnsProps) => {
  const userStore = useStore('user');
  const getButtonText = (btn: BaseButtonConfig): ReactNode => {
    if (btn.type === 'delete') {
      return btn.text || '删除';
    }
    if (btn.type === 'edit') {
      return btn.text || '编辑';
    }
    if (btn.type === 'common') {
      return btn.text;
    }
    return '';
  };

  const isVisible = (show?: boolean | (() => boolean)): boolean => {
    if (show === undefined) return true;
    return typeof show === 'function' ? show() : show;
  };

  const isDisabled = (disabled?: boolean | (() => boolean)): boolean => {
    if (disabled === undefined) return false;
    return typeof disabled === 'function' ? disabled() : disabled;
  };

  const getButtonProps = (btn: BaseButtonConfig): ButtonProps => {
    const { type, show, disabled, onClick, text, permission, ...restProps } = btn as any;

    const baseProps: ButtonProps = {
      type: 'link',
      // danger: type === 'delete',
      ...restProps,
      disabled: isDisabled(disabled),
      onClick: onClick,
      style: { padding: '0 6px', ...restProps.style },
    };

    if (type === 'delete') {
      return {
        ...baseProps,
        // danger: true,
      };
    }

    return baseProps;
  };
  const isPermissioned = (btn: BaseButtonConfig): boolean => {
    if (btn.permission && btn.permission.length > 0) {
      return contains(userStore.info?.permissions || [], btn.permission);
    }
    return true;
  }

  const visibleBtns = btns.filter((btn) => isVisible(btn.show) && isPermissioned(btn));

  if (visibleBtns.length === 0) {
    return null;
  }

  const renderButton = (btn: BaseButtonConfig, index: number) => {
    const buttonElement = (
      <Button key={index} {...getButtonProps(btn)}>
        {getButtonText(btn)}
      </Button>
    );

    if (btn.permission && btn.permission.length > 0) {
      return (
        <AccessButton key={index} permission={btn.permission}>
          {buttonElement}
        </AccessButton>
      );
    }

    return buttonElement;
  };
  // 如果按钮数量 <= 3，直接全部显示
  if (visibleBtns.length <= 3) {
    return <Space size={0}>{visibleBtns.map((btn, index) => renderButton(btn, index))}</Space>;
  }

  // 如果按钮数量 > 3，前3个显示，剩余的放到 Dropdown 中
  const displayBtns = visibleBtns.slice(0, 3);
  const dropdownBtns = visibleBtns.slice(3);

  const menuItems: MenuProps['items'] = dropdownBtns.map((btn, index) => ({
    key: index,
    label: getButtonText(btn),
    disabled: isDisabled(btn.disabled),
    danger: btn.type === 'delete' || btn.danger,
    onClick: () => {
      if (btn.onClick) {
        btn.onClick();
      }
    },
  }));

  return (
    <Space size={0}>
      {displayBtns.map((btn, index) => renderButton(btn, index))}
      <Dropdown menu={{ items: menuItems }} placement="bottomRight">
        <Button type="link" style={{ padding: '0 8px' }}>更多</Button>
      </Dropdown>
    </Space>
  );
};

export default observer(OperatorBtns);
